import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { toast } from 'react-hot-toast';
import Header from 'shared/components/Header';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import Table from 'shared/components/Table';
import { tokens } from 'shared/theme';
import { useAuth } from 'shared/hooks/useAuth';
import { canViewAllRecords } from 'shared/utils/canViewAllRecords';
import { listStatusParams, monitoringReviewApi } from '../../api/systemRegisterApi';
import { apiErrorMessage, unwrapList } from '../../utils/responseHelpers';
import MonitoringReviewMasterDialog, {
  emptyMonitoringReviewMaster,
} from '../../shared/MonitoringReviewMasterDialog';
import { renderEnumChip, renderRecordStatusChip } from '../../shared/statusChips';
import { sortRowsNewestFirst } from 'shared/utils/sortRowsNewestFirst';

const isDeleted = (row) => String(row?.status || '').toUpperCase() === 'DELETED';

export default function ReviewsPage() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user } = useAuth();
  const canViewAll = canViewAllRecords(user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await monitoringReviewApi.list(listStatusParams(canViewAll && showAll));
      const list = unwrapList(response);
      setRows(
        list.map((item) => ({
          id: item.id,
          monitoringType: item.monitoringType ?? '',
          reviewAreaControl: item.reviewAreaControl ?? '',
          standardControlReference: item.standardControlReference ?? '',
          reviewFrequency: item.reviewFrequency ?? '',
          status: item.status ?? 'ACTIVE',
          createdAt: item.createdAt ?? '',
          updatedAt: item.updatedAt ?? '',
        }))
      );
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to load monitoring reviews.'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [canViewAll, showAll]);

  useEffect(() => {
    if (!canViewAll && showAll) {
      setShowAll(false);
    }
  }, [canViewAll, showAll]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setEditing(emptyMonitoringReviewMaster());
    setDialogOpen(true);
  };

  const openEdit = useCallback((row) => {
    if (isDeleted(row)) return;
    setEditing({
      ...emptyMonitoringReviewMaster(),
      ...row,
      id: row.id,
    });
    setDialogOpen(true);
  }, []);

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const payload = {
        monitoringType: values.monitoringType,
        reviewAreaControl: values.reviewAreaControl || undefined,
        standardControlReference: values.standardControlReference || undefined,
        reviewFrequency: values.reviewFrequency || undefined,
      };
      if (values.id != null) {
        await monitoringReviewApi.update(values.id, payload);
        toast.success('Monitoring review updated');
      } else {
        await monitoringReviewApi.create(payload);
        toast.success('Monitoring review created');
      }
      setDialogOpen(false);
      setEditing(null);
      await load();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to save monitoring review.'));
    } finally {
      setSaving(false);
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget?.id) return;
    setRemoving(true);
    try {
      await monitoringReviewApi.remove(removeTarget.id);
      toast.success('Monitoring review marked as deleted');
      setRemoveTarget(null);
      await load();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to delete monitoring review.'));
    } finally {
      setRemoving(false);
    }
  };

  const pagedRows = useMemo(() => {
    const ordered = sortRowsNewestFirst(rows);
    const start = page * size;
    return ordered.slice(start, start + size);
  }, [rows, page, size]);

  const columns = useMemo(
    () => [
      {
        field: 'monitoringType',
        headerName: 'Monitoring type',
        flex: 1,
        minWidth: 180,
        renderCell: renderEnumChip,
      },
      {
        field: 'reviewAreaControl',
        headerName: 'Review area / control',
        flex: 1.3,
        minWidth: 180,
        valueFormatter: (params) => params?.value || '—',
      },
      {
        field: 'standardControlReference',
        headerName: 'Standard control reference',
        flex: 1.2,
        minWidth: 170,
        valueFormatter: (params) => params?.value || '—',
      },
      {
        field: 'reviewFrequency',
        headerName: 'Review frequency',
        flex: 0.9,
        minWidth: 150,
        renderCell: renderEnumChip,
      },
      ...(showAll && canViewAll
        ? [
            {
              field: 'status',
              headerName: 'Status',
              width: 130,
              renderCell: ({ row }) =>
                renderRecordStatusChip({
                  value: isDeleted(row) ? 'DELETED' : row.status || 'ACTIVE',
                  row,
                }),
            },
          ]
        : []),
      {
        field: 'actions',
        headerName: 'Actions',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const deleted = isDeleted(row);
          return (
            <Box display="flex" alignItems="center" gap={0.25}>
              <Tooltip title="Edit">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => openEdit(row)}
                    disabled={deleted}
                    aria-label="Edit"
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={deleted ? 'Already deleted' : 'Mark as deleted'}>
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setRemoveTarget(row)}
                    disabled={deleted}
                    aria-label="Delete"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [canViewAll, openEdit, showAll]
  );

  return (
    <Box m="20px">
      <Header title="Reviews" subtitle="Monitoring review catalog for IT governance" />

      <Box
        mb={2}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        flexWrap="wrap"
      >
        {canViewAll ? (
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Button
            color="secondary"
            variant={showAll ? 'contained' : 'outlined'}
            size="small"
            sx={{ fontSize: '13px', py: 0.75, px: 1.5 }}
            onClick={() => {
              setShowAll(true);
              setPage(0);
            }}
          >
            View all
          </Button>
          <Button
            color="secondary"
            variant={!showAll ? 'contained' : 'outlined'}
            size="small"
            sx={{ fontSize: '13px', py: 0.75, px: 1.5 }}
            onClick={() => {
              setShowAll(false);
              setPage(0);
            }}
          >
            Active only
          </Button>
        </Box>
        ) : (
          <Box />
        )}

        <Button
          color="secondary"
          variant="contained"
          size="small"
          sx={{ fontSize: '13px', py: 0.75, px: 1.5 }}
          onClick={openAdd}
          disabled={saving}
        >
          Add monitoring review
        </Button>
      </Box>

      {loading ? (
        <LoadingSpinner message="Loading monitoring reviews…" height="300px" />
      ) : (
        <Table
          tableId="system-register.monitoring-reviews"
          title="Reviews"
          rows={pagedRows}
          columns={columns}
          totalRecords={rows.length}
          size={size}
          page={page}
          handlePageChange={setPage}
          colors={colors}
        />
      )}

      <MonitoringReviewMasterDialog
        open={dialogOpen}
        initialValues={editing}
        saving={saving}
        onClose={() => {
          if (saving) return;
          setDialogOpen(false);
          setEditing(null);
        }}
        onSave={handleSave}
      />

      <Dialog open={Boolean(removeTarget)} onClose={() => !removing && setRemoveTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete monitoring review?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Do you want to delete the monitoring review?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRemoveTarget(null)} disabled={removing}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={confirmRemove} disabled={removing}>
            {removing ? 'Deleting…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
